import { Suspense, useState } from "react";
import { App } from "@zapier/types";
import {
  AppPicker,
  appsByCategoryAtomFamily_SUSPENDS,
} from "@zapier/app-picker";
import { useAtomValue, useSetAtom } from "jotai";
import { emitUserInteractionEventSetter } from "@zapier/canvas-state";

import { ErrorBoundary } from "../../components/ErrorBoundary";

import styles from "./AppSelection.module.css";

export type Category = "spreadsheets" | "email" | "crm" | "forms" | "databases";

export type SimpleApp = {
  name: string;
  icon: string;
  category?: Category;
};

function SuspendedHiddenAppPreFetcher({
  category,
}: Pick<SimpleApp, "category">) {
  useAtomValue(appsByCategoryAtomFamily_SUSPENDS(category));
  return null;
}

function HiddenAppPrefetcher({ category }: Pick<SimpleApp, "category">) {
  return (
    <Suspense fallback={null}>
      <SuspendedHiddenAppPreFetcher category={category} />
    </Suspense>
  );
}

export const AppSelection = ({
  app,
  setApp,
}: {
  app: SimpleApp;
  setApp: (app: SimpleApp) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);

  const renderAppPicker = () => {
    if (isOpen) {
      const handleSelect = (newApp: App) => {
        setApp({
          name: newApp.name,
          icon: newApp.images.url_128x128,
        });
        setIsOpen(false);

        emitUserInteractionEvent({
          eventAction: "click",
          interactionGoal: "App selection changed",
          rawText: `Changed from ${app.name} to ${newApp.name}`,
        });
      };

      const handleClose = () => {
        setIsOpen(false);
      };

      return (
        <div className={styles.appPickerWrapper}>
          <AppPicker
            onSelect={handleSelect}
            onClose={handleClose}
            category={app.category}
            selectedApp={app.name}
            hideZapOption={true}
          />
        </div>
      );
    }
  };

  return (
    <div className={styles.appSelection}>
      <HiddenAppPrefetcher category={app.category} />
      <button
        className={styles.integrationName}
        onClick={() => setIsOpen(true)}
        aria-label={`View ${app.name} details`}
      >
        {app.name}
      </button>
      <ErrorBoundary>{renderAppPicker()}</ErrorBoundary>
    </div>
  );
};
